// Two Shot brand components — faithful ports of the design system's signature
// pieces: BlendMeter, TicketStub, AvatarPair, StampBadge, PosterCard. They use
// the design tokens (CSS vars declared in styles.css).

import type { CSSProperties, ReactNode } from "react";

// ---- BlendMeter: gold→red compatibility readout (ring or bar) --------------

export function BlendMeter({
  value = 50,
  variant = "ring",
  size = 132,
  label = "Blend",
  style = {},
}: {
  value?: number;
  variant?: "ring" | "bar";
  size?: number;
  label?: string;
  style?: CSSProperties;
}) {
  const pct = Math.max(0, Math.min(100, Math.round(value)));

  if (variant === "bar") {
    return (
      <div style={{ width: "100%", fontFamily: "var(--font-body)", ...style }} data-testid="blend-meter">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "currentColor", opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 15, fontWeight: 700, color: "currentColor" }}>{pct}%</span>
        </div>
        <div style={{ height: 12, borderRadius: "var(--r-pill)", background: "var(--surface-sunk)", border: "1px solid var(--border-soft)", overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", borderRadius: "var(--r-pill)", background: "linear-gradient(90deg, var(--gold-500), var(--red-500))", transition: "width var(--dur-slow) var(--ease-out)" }} />
        </div>
      </div>
    );
  }

  const stroke = Math.max(8, size * 0.09);
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const off = circ * (1 - pct / 100);

  return (
    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 6, fontFamily: "var(--font-body)", ...style }} data-testid="blend-meter">
      <div style={{ position: "relative", width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-sunk)" strokeWidth={stroke} />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="url(#blendgrad)"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={off}
            style={{ transition: "stroke-dashoffset var(--dur-slow) var(--ease-out)" }}
          />
          <defs>
            <linearGradient id="blendgrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="var(--gold-500)" />
              <stop offset="100%" stopColor="var(--red-600)" />
            </linearGradient>
          </defs>
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: size * 0.26, color: "currentColor", lineHeight: 1 }}>{pct}%</span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "currentColor", opacity: 0.55, marginTop: 2 }}>{label}</span>
        </div>
      </div>
    </div>
  );
}

// ---- TicketStub: perforated ticket surface ---------------------------------

export function TicketStub({
  children,
  stub = null,
  tone = "paper",
  style = {},
  testid,
}: {
  children: ReactNode;
  stub?: ReactNode;
  tone?: "paper" | "ink" | "red";
  style?: CSSProperties;
  testid?: string;
}) {
  const tones: Record<string, CSSProperties> = {
    paper: { background: "var(--surface-card)", color: "var(--text-primary)", border: "1px solid var(--border-soft)" },
    ink: { background: "var(--ink-900)", color: "var(--text-inverse)", border: "1px solid var(--ink-800)" },
    red: { background: "var(--brand)", color: "var(--text-on-brand)", border: "1px solid var(--brand-strong)" },
  };
  const t = tones[tone] || tones.paper;
  const notch = "var(--surface-page)";
  return (
    <div
      data-testid={testid}
      style={{ display: "flex", borderRadius: "var(--r-lg)", overflow: "hidden", boxShadow: "var(--shadow-card)", position: "relative", fontFamily: "var(--font-body)", ...t, ...style }}
    >
      <div style={{ flex: 1, padding: "20px 22px" }}>{children}</div>
      {stub != null && (
        <div style={{ position: "relative", display: "flex", alignItems: "center", padding: "20px 18px", borderLeft: `2px dashed ${tone === "paper" ? "var(--border-strong)" : "rgba(255,255,255,0.35)"}` }}>
          <span style={{ position: "absolute", top: -8, left: -9, width: 16, height: 16, borderRadius: "50%", background: notch }} />
          <span style={{ position: "absolute", bottom: -8, left: -9, width: 16, height: 16, borderRadius: "50%", background: notch }} />
          {stub}
        </div>
      )}
    </div>
  );
}

// ---- AvatarPair: two overlapping avatars (the "two shot") -------------------

export function AvatarPair({
  a = {},
  b = {},
  size = 48,
  overlap = 0.38,
  style = {},
}: {
  a?: { name?: string; initial?: string; src?: string };
  b?: { name?: string; initial?: string; src?: string };
  size?: number;
  overlap?: number;
  style?: CSSProperties;
}) {
  const ring = Math.max(2, size * 0.05);
  const bump = -(size * overlap);
  const Av = ({ person, accent }: { person: { name?: string; initial?: string; src?: string }; accent: "red" | "gold" }) => (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        border: `${ring}px solid var(--surface-page)`,
        background: person.src
          ? `center/cover no-repeat url(${person.src})`
          : `linear-gradient(150deg, ${accent === "red" ? "var(--red-500)" : "var(--gold-500)"}, ${accent === "red" ? "var(--red-700)" : "var(--clay-500)"})`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--paper-50)",
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: size * 0.4,
        boxShadow: "var(--shadow-sm)",
      }}
    >
      {!person.src && (person.initial || (person.name ? person.name[0]?.toUpperCase() : "?"))}
    </div>
  );
  return (
    <div style={{ display: "inline-flex", alignItems: "center", ...style }} data-testid="avatar-pair">
      <Av person={a} accent="red" />
      <div style={{ marginLeft: bump }}>
        <Av person={b} accent="gold" />
      </div>
    </div>
  );
}

// ---- StampBadge: rotated rubber-stamp label --------------------------------

export function StampBadge({
  children,
  tone = "red",
  rotate = -6,
  style = {},
}: {
  children: ReactNode;
  tone?: "red" | "gold" | "ink" | "green";
  rotate?: number;
  style?: CSSProperties;
}) {
  const tones: Record<string, string> = {
    red: "var(--brand)",
    gold: "var(--gold-600)",
    ink: "var(--ink-900)",
    green: "var(--green-500)",
  };
  const c = tones[tone] || tones.red;
  return (
    <span
      data-testid="stamp-badge"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 12px",
        border: `2px solid ${c}`,
        borderRadius: "var(--r-sm)",
        color: c,
        fontFamily: "var(--font-mono)",
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        transform: `rotate(${rotate}deg)`,
        background: "transparent",
        ...style,
      }}
    >
      {children}
    </span>
  );
}

// ---- PosterCard: film tile with warm duotone fallback ----------------------

const POSTER_DUOTONES = [
  "linear-gradient(160deg, #C4623E, #85160F)",
  "linear-gradient(160deg, #E0A526, #A84A1E)",
  "linear-gradient(160deg, #8A2B22, #2A1410)",
  "linear-gradient(160deg, #D0764F, #6E2313)",
  "linear-gradient(160deg, #B3822A, #4A2C12)",
  "linear-gradient(160deg, #A81E1A, #3C1208)",
];
function posterHash(str: string) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function PosterCard({
  title = "Untitled",
  year,
  poster,
  rating,
  overlap = false,
  badge,
  width = 150,
  style = {},
}: {
  title?: string;
  year?: number;
  poster?: string | null;
  rating?: number;
  overlap?: boolean;
  badge?: string;
  width?: number | string;
  style?: CSSProperties;
}) {
  return (
    <div style={{ width, display: "flex", flexDirection: "column", gap: 8, ...style }} data-testid="poster">
      <div
        style={{
          position: "relative",
          aspectRatio: "2 / 3",
          borderRadius: "var(--r-md)",
          overflow: "hidden",
          background: poster ? `center/cover no-repeat url(${poster})` : POSTER_DUOTONES[posterHash(title) % POSTER_DUOTONES.length],
          border: "1px solid var(--border-ink)",
          boxShadow: "var(--shadow-md)",
        }}
      >
        {!poster && (
          <span style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "space-between", padding: rating != null ? "10px 10px 34px" : "10px", color: "var(--paper-50)", fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15, lineHeight: 1.15, boxSizing: "border-box" }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.16em", opacity: 0.75 }}>{year || " "}</span>
            <span>{title}</span>
          </span>
        )}
        {overlap && (
          <span style={{ position: "absolute", top: 8, left: 8, background: "var(--accent)", color: "var(--ink-900)", fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "3px 7px", borderRadius: "var(--r-pill)" }}>Both</span>
        )}
        {badge && !overlap && (
          <span style={{ position: "absolute", top: 8, left: 8, background: "var(--gold-500)", color: "var(--ink-900)", fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", padding: "3px 7px", borderRadius: "var(--r-pill)" }}>{badge}</span>
        )}
        {rating != null && (
          <span style={{ position: "absolute", bottom: 8, right: 8, background: "rgba(28,23,18,0.82)", color: "var(--accent)", fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700, padding: "3px 7px", borderRadius: "var(--r-pill)" }}>★ {rating}</span>
        )}
      </div>
      {/* Caption only when there's real poster art — the duotone fallback already
          carries the title, so a caption would duplicate it. */}
      {poster ? (
        <div>
          <div style={{ fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 600, color: "var(--text-primary)", lineHeight: 1.25 }}>{title}</div>
          {year && <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>{year}</div>}
        </div>
      ) : null}
    </div>
  );
}

/** Wordmark: two overlapping dots + "Two" / gold "Shot". */
export function Wordmark({ size = 18, inverse = false }: { size?: number; inverse?: boolean }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span style={{ display: "inline-flex" }} aria-hidden>
        <span style={{ width: size * 1.05, height: size * 1.05, borderRadius: "50%", background: "var(--red-500)" }} />
        <span style={{ width: size * 1.05, height: size * 1.05, borderRadius: "50%", background: "var(--gold-500)", marginLeft: -(size * 0.55) }} />
      </span>
      <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: size, letterSpacing: "-0.02em", color: inverse ? "var(--paper-50)" : "var(--ink-900)" }}>
        Two<span style={{ color: "var(--gold-500)" }}>Shot</span>
      </span>
    </span>
  );
}
